import { LoadingButton } from '@mui/lab';
import type { SxProps, Theme } from '@mui/material';
import { Box } from '@mui/material';
import { useCallback } from 'react';
import { SelectedCount } from '~/components/data-grid/SelectedCount';
import { SearchBar } from '~/components/forms/search-bar';
import type { RowSelectionMode } from '~/hooks/data-grid';

type ActionBarOrientation = 'default' | 'inverse';

interface SelectableListToolbarProps {
  actionBarOrientation: ActionBarOrientation;
  actionIcon?: React.ReactNode;
  actionLabel?: string;
  actionLoading: boolean;
  onActionClick?: () => void;
  rowSelectionMode: RowSelectionMode;
  search: string;
  searchPlaceholder?: string;
  onSearchChange: (search: string) => void;
  selectedIds: number[];
  thing: string;
  totalCount: number;
}

export const SelectableListToolbar = ({
  actionBarOrientation,
  actionIcon,
  actionLabel,
  actionLoading,
  onActionClick,
  rowSelectionMode,
  search,
  searchPlaceholder = 'Search',
  onSearchChange,
  selectedIds,
  thing,
  totalCount,
}: SelectableListToolbarProps) => {
  const handleSearchChange = useCallback(
    (value: string) => onSearchChange(value),
    [onSearchChange],
  );

  const searchField = (
    <SearchBar placeholder={searchPlaceholder} search={search} onChange={handleSearchChange} />
  );

  const actionBox = (
    <Box alignItems="center" gap={2} display="flex">
      {actionBarOrientation === 'inverse' ? (
        <>
          {onActionClick && actionLabel && (
            <LoadingButton
              variant="contained"
              disabled={selectedIds.length === 0}
              onClick={onActionClick}
              loading={actionLoading}
              startIcon={actionIcon}
              sx={{ whiteSpace: 'nowrap' }}
            >
              {actionLabel}
            </LoadingButton>
          )}
          <SelectedCount
            rowSelectionMode={rowSelectionMode}
            selectedCount={selectedIds.length}
            thing={thing}
            totalCount={totalCount}
          />
        </>
      ) : (
        <>
          <SelectedCount
            rowSelectionMode={rowSelectionMode}
            selectedCount={selectedIds.length}
            thing={thing}
            totalCount={totalCount}
          />
          {onActionClick && actionLabel && (
            <LoadingButton
              variant="contained"
              disabled={selectedIds.length === 0}
              onClick={onActionClick}
              loading={actionLoading}
              endIcon={actionIcon}
              sx={{ whiteSpace: 'nowrap' }}
            >
              {actionLabel}
            </LoadingButton>
          )}
        </>
      )}
    </Box>
  );

  return (
    <Box
      display="flex"
      flexDirection={{ xs: 'column', md: 'row' }}
      justifyContent="space-between"
      alignItems={{ xs: 'stretch', md: 'center' }}
      my={1}
      gap={2}
    >
      {actionBarOrientation === 'inverse' ? (
        <>
          {actionBox}
          {searchField}
        </>
      ) : (
        <>
          {searchField}
          <Box display="flex" justifyContent="flex-end" gap={2}>
            {actionBox}
          </Box>
        </>
      )}
    </Box>
  );
};

interface SelectableListProps {
  children: React.ReactNode;
  sx?: SxProps<Theme>;
  title: string;
}

export const SelectableList = ({ children, sx, title }: SelectableListProps) => {
  return (
    <Box
      width={{ xs: '100%' }}
      p={2}
      display="flex"
      flexDirection="column"
      flex={1}
      minWidth={0}
      minHeight={0}
      mb={2}
      borderRadius={1}
      boxShadow={1}
      sx={{ bgcolor: 'background.paper', ...sx }}
    >
      <h2>{title}</h2>
      {children}
    </Box>
  );
};
